import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Set Up Next.js Application',
    description: 'Initialize a new Next.js application with TypeScript support and essential configurations.',
    requirements: [
      {
        text: 'Create a Node 20-compatible Next.js application'
      },
      {
        text: 'Configure TypeScript for type safety'
      },
      {
        text: 'Set up the following directory structure:',
        subitems: ['src/app for App Router pages', 'src/components for reusable components', 'src/lib for utility functions', 'public for static assets']
      },
      {
        text: 'Install essential dependencies for full-stack development'
      },
      {
        text: 'Configure ESLint and Prettier for code quality'
      }
    ],
    verification: [
      'Next.js application runs on port 3000',
      'TypeScript configuration is properly set up',
      'Directory structure follows Next.js best practices',
      'Development server starts without errors',
      'Hot reload functionality works correctly'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Configure Database Connection',
    description: 'Set up PostgreSQL database with Prisma ORM for data persistence and management.',
    requirements: [
      {
        text: 'Install and configure PostgreSQL database'
      },
      {
        text: 'Set up Prisma ORM with the following:',
        subitems: ['Initialize Prisma with PostgreSQL provider', 'Create database schema file with User and Post models', 'Configure DATABASE_URL=postgresql://nextapp:nextpass123@localhost:5432/nextapp', 'Set up Prisma Client generation']
      },
      {
        text: 'Create initial database schema with User and Post models'
      },
      {
        text: 'Run database migrations so User and Post tables exist in PostgreSQL'
      },
      {
        text: 'Seed database with sample users and posts'
      }
    ],
    verification: [
      'PostgreSQL database is running and accessible',
      'Prisma schema is valid and migrations run successfully',
      'Database tables are created with proper relationships',
      'Prisma Client generates without errors',
      'Sample data is successfully seeded in database'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Build API Routes',
    description: 'Create RESTful API endpoints using Next.js App Router for backend functionality.',
    requirements: [
      {
        text: 'Create API routes for the following operations:',
        subitems: [
          'GET /api/users - List users (required for grading)',
          'GET /api/posts - List posts (required for grading)',
          'GET /api/posts/[id] - Get single post',
          'POST /api/posts - Create new post',
          'PUT /api/posts/[id] - Update post',
          'DELETE /api/posts/[id] - Delete post'
        ]
      },
      {
        text: 'Implement proper error handling and validation'
      },
      {
        text: 'Add middleware for request logging'
      },
      {
        text: 'Configure CORS for API access'
      },
      {
        text: 'Test all endpoints with sample requests'
      }
    ],
    verification: [
      'All API endpoints respond with correct status codes',
      'CRUD operations work correctly with database',
      'Error responses include appropriate error messages',
      'API routes handle edge cases gracefully',
      'Request logging captures all API calls'
    ]
  },
  {
    id: 'task-4a',
    number: 4,
    title: 'Authentication Configuration (NextAuth paths)',
    description: 'Ensure NextAuth is configured at a standard, discoverable path for API routes.',
    requirements: [
      { text: 'Install and configure NextAuth.js if not already done' },
      {
        text: 'Place NextAuth config at a standard path (required for grading):',
        subitems: [
          'pages/api/auth/[...nextauth].ts (Pages router) or',
          'app/api/auth/[...nextauth]/route.ts (App router)'
        ]
      },
      { text: 'Configure providers, session strategy, and callbacks' }
    ],
    verification: [
      'NextAuth config file exists at one of the standard paths',
      'Auth endpoints are reachable',
      'Sessions establish successfully'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Create Frontend Components',
    description: 'Build React components for the user interface with proper styling and interactivity.',
    requirements: [
      {
        text: 'Create the following UI components:',
        subitems: ['Navigation header with menu items', 'Post list component with pagination', 'Post detail view component', 'Post creation/edit form', 'Loading and error states']
      },
      {
        text: 'Implement responsive design using Tailwind CSS'
      },
      {
        text: 'Add client-side form validation'
      },
      {
        text: 'Integrate components with API routes'
      },
      {
        text: 'Implement proper loading and error states'
      }
    ],
    verification: [
      'All components render without errors',
      'Responsive design works on mobile and desktop',
      'Forms validate input before submission',
      'Components properly handle API responses',
      'Loading states display during data fetching'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Implement Authentication',
    description: 'Add user authentication using NextAuth.js with secure session management.',
    requirements: [
      {
        text: 'Install and configure NextAuth.js'
      },
      {
        text: 'Set up authentication providers:',
        subitems: ['Credentials provider for email/password', 'OAuth provider (GitHub or Google)', 'Configure session strategy', 'Set up callback URLs']
      },
      {
        text: 'Create authentication pages (login, register, profile)'
      },
      {
        text: 'Protect API routes with authentication middleware'
      },
      {
        text: 'Implement user session management'
      }
    ],
    verification: [
      'Users can register and login successfully',
      'OAuth authentication works correctly',
      'Protected routes require authentication',
      'Session persists across page refreshes',
      'Logout functionality clears session properly'
    ]
  },
  {
    id: 'task-6',
    number: 6,
    title: 'Deploy Application',
    description: 'Prepare and run the full-stack Next.js application locally in production mode with reproducible container configuration.',
    requirements: [
      {
        text: 'Prepare application for local production execution:',
        subitems: ['Optimize build with npm run build', 'Configure environment variables with the lab database URL', 'Implement security headers where appropriate', 'Verify `.next/BUILD_ID` exists after the build']
      },
      {
        text: 'Keep the PostgreSQL-backed application reachable on port 3000'
      },
      {
        text: 'Provide a Dockerfile for the application'
      },
      {
        text: 'Provide docker-compose.yml with application and PostgreSQL service definitions'
      },
      {
        text: 'Set up monitoring and analytics'
      }
    ],
    verification: [
      'Application builds without errors and creates `.next/BUILD_ID`',
      'Application is reachable locally on port 3000',
      'Database connection works with the lab PostgreSQL credentials',
      'Dockerfile and docker-compose.yml are present and usable',
      'TypeScript check completes without errors'
    ]
  }
];

const NextjsFullStackInstructions: React.FC = () => {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setCurrentTaskIndex(0);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);
    
    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }
    
    setCompletedChecks(newChecks);

    // Check if all verifications for this task are completed
    const currentTask = tasks[currentTaskIndex];
    const allChecksCompleted = currentTask.verification.every(
      (_, idx) => newChecks.has(`${currentTask.id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>
            
            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>
            
            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Footer */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>
        
        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="flex gap-[10px]">
          <button
            onClick={handlePreviousTask}
            disabled={currentTaskIndex === 0}
            className="px-4 py-2 bg-[#2d3748] border border-[#374151] text-[#cbd5e1] rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#374151] hover:text-[#e2e8f0] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNextTask}
            className="px-4 py-2 bg-[#10b981] border border-[#10b981] text-[#1a1f2e] font-semibold rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#059669] hover:border-[#059669]"
          >
            {currentTaskIndex === tasks.length - 1 ? 'Return to Task 1' : 'Next Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NextjsFullStackInstructions;
